import React, { useEffect, useMemo, useRef, useState } from 'react';
import './splash.css';

/**
 * Splash overlay that shows for a fixed 2s, then fades out and removes itself.
 * - Primary source: loader_logo from /api/logo (resolved via {API_URL}/storage/{loader_logo})
 * - Falls back to /splash/{domain}.gif, then /splash/default.gif
 * - If all fail, shows a three-dots loader
 * - Calls onSplashComplete after fade finishes
 */
export default function Splash({ onSplashComplete }) {
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);
  // resolvedSrc states:
  // undefined = still determining (loading)
  // null = API + domain-specific + default all failed (show loader)
  // string = resolved image path
  const [resolvedSrc, setResolvedSrc] = useState(undefined);
  const doneRef = useRef(false);

  const domainKey = useMemo(() => {
    try {
      const host = (window.location.hostname || '').toLowerCase().replace(/^www\./, '');
      return host.split('.')[0] || 'default';
    } catch {
      return 'default';
    }
  }, []);

  const defaultSrc = '/splash/default.gif';
  const staticFallbacks = [`/splash/${domainKey}.gif`, defaultSrc];

  // Load splash from the API first (loader_logo from /api/logo). Only if the
  // API is unavailable / has no loader_logo / the image fails do we fall back
  // to the static /splash/{domain}.gif -> /splash/default.gif chain.
  useEffect(() => {
    let cancelled = false;
    setResolvedSrc(undefined); // reset while probing

    // Try each static fallback in order.
    const tryStatic = (index = 0) => {
      if (cancelled) return;
      if (index >= staticFallbacks.length) {
        setResolvedSrc(null);
        return;
      }
      const src = staticFallbacks[index];
      const img = new Image();
      img.onload = () => { if (!cancelled) setResolvedSrc(src); };
      img.onerror = () => { if (!cancelled) tryStatic(index + 1); };
      img.src = src;
    };

    // Try the API-provided loader_logo first.
    const tryApi = async () => {
      try {
        if (!process.env.REACT_APP_API_URL) return tryStatic();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/logo`);
        if (!response.ok) return tryStatic();
        const data = await response.json();
        // Response may be an array ([{...}]) or an error object ({message: ...}).
        const item = Array.isArray(data) ? data[0] : null;
        const loaderLogo = item?.loader_logo;
        if (!loaderLogo) return tryStatic();

        const apiSrc = loaderLogo.startsWith('http')
          ? loaderLogo
          : `${process.env.REACT_APP_API_URL}/storage/${loaderLogo.replace(/^\/?storage\//i, '')}`;

        const img = new Image();
        img.onload = () => { if (!cancelled) setResolvedSrc(apiSrc); };
        img.onerror = () => { if (!cancelled) tryStatic(); };
        img.src = apiSrc;
      } catch (err) {
        console.debug('Splash: API logo failed, falling back to static', err);
        if (!cancelled) tryStatic();
      }
    };

    tryApi();
    return () => { cancelled = true; };
  }, [domainKey]);

  // Fixed 2s timer then fade 500ms, then complete
  useEffect(() => {
    const showTimer = setTimeout(() => {
      setFading(true);
      const fadeTimer = setTimeout(() => {
        if (!doneRef.current) {
          doneRef.current = true;
          setVisible(false);
          onSplashComplete && onSplashComplete();
          // Nudge layout once content becomes visible
          setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
        }
      }, 500);
      return () => clearTimeout(fadeTimer);
    }, 2000);
    return () => clearTimeout(showTimer);
  }, [onSplashComplete]);

  if (!visible) return null;

  const className = `splash-screen${fading ? ' fade-out' : ''}`;
  const isFullBackground = domainKey === 'ck4' || domainKey === 'sona666' || domainKey === 'ck999' || domainKey === 'sk444' || domainKey === 'stk111';

  const showLoader = resolvedSrc === null || resolvedSrc === undefined;

  return (
    <div
      className={className}
      role="status"
      aria-live="polite"
      aria-label="Loading"
      style={isFullBackground && resolvedSrc ? {
        backgroundImage: `url(${resolvedSrc})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      } : undefined}
    >
      {isFullBackground ? null : (
        resolvedSrc ? (
          <img src={resolvedSrc} alt="splash" style={{ maxWidth: '80%', maxHeight: '80%' }} />
        ) : showLoader ? (
          <div className="splash-dots" aria-hidden="true">
            <span></span><span></span><span></span>
          </div>
        ) : null
      )}
    </div>
  );
}
